import { FC } from "react";
import { HiChevronDown } from "react-icons/hi";
import { PiMapPin } from 'react-icons/pi';
import { Pickup } from "../../../../type/entities/pickup-type";
import cn from "../../../../utils/classNames";
import { pickupStatuses } from "../../../../utils/SelectorMapping";
import PickupCard from "./PickupCard";

type PickupWithNearby = Pickup & {
    nearby?: (Pickup & { distanceKm?: number })[];
};

interface RouteGroupData {
    routeId: string;
    routeName: string;
    routeDescription?: string;
    pickups: PickupWithNearby[];
    totalDrums: number;
    estimatedTime: string;
}

interface RouteGroupProps {
    route: RouteGroupData;
    isOpen: boolean;
    openNearby: Record<string, boolean>;
    downloadingReceipt: Record<string, boolean>;
    downloadingTicket: Record<string, boolean>;
    onToggleRoute: (routeId: string) => void;
    onCompletePickup: (pickup: Pickup) => void;
    onFailPickup: (pickup: Pickup) => void;
    onSignPickup: (pickupId: string) => void;
    onToggleNearby: (pickupId: string) => void;
    onGenerateReceipt: (pickupId: string, nameHeadquarter: string) => void;
    onGenerateTicket: (pickupId: string, nameHeadquarter: string) => void;
    onPrintTicket: (pickupId: string) => void;
    onRefresh?: () => void;
    onAssignPickup?: (pickup: Pickup) => void;
}

const RouteGroup: FC<RouteGroupProps> = ({
    route,
    isOpen,
    openNearby,
    downloadingReceipt,
    downloadingTicket,
    onToggleRoute,
    onCompletePickup,
    onFailPickup,
    onSignPickup,
    onToggleNearby,
    onGenerateReceipt,
    onGenerateTicket,
    onPrintTicket,
    onRefresh,
    onAssignPickup
}) => {
    return (
        <div className="bg-white rounded-lg border overflow-hidden route-group shadow-md">
            {/* Header de ruta */}
            <button
                type="button"
                onClick={() => onToggleRoute(route.routeId)}
                className="bg-white w-full px-4 py-3 border-b flex items-center justify-between text-left hover:bg-gray-100"
                aria-expanded={isOpen}
                aria-controls={`route-panel-${route.routeId}`}
            >
                <div className="flex-1">
                    <div className="font-semibold text-gray-900 flex items-center gap-2">
                        <PiMapPin className="h-4 w-4 text-blue-600" />
                        {route.routeName}
                    </div>
                    {route.routeDescription && (
                        <div className="text-sm text-gray-500 mt-1">{route.routeDescription}</div>
                    )}
                </div>
                <div className="text-right flex items-center gap-3">
                    <div>
                        <div className="text-sm font-medium text-gray-900">{route.pickups.length} recogidas</div>
                        <div className="text-xs text-gray-500">~{route.estimatedTime}</div>
                    </div>
                    <HiChevronDown className={cn("h-5 w-5 transition-transform", isOpen && "rotate-180")} />
                </div>
            </button>

            {/* Lista de recogidas */}
            <div id={`route-panel-${route.routeId}`} className={cn("bg-gray-50 divide-y divide-gray-300 collapsible overflow-auto", isOpen && "open")}>
                {route.pickups.map((pickup, index) => (
                    <div className="relative">
                        <PickupCard
                            containerClassName={cn('planned' === pickupStatuses.find(status => status.value === pickup?.pickupStatus)?.value ? 'blur-md pointer-events-none' : '')}
                            key={pickup.id}
                            pickup={pickup}
                            index={index}
                            isNearbyOpen={!!openNearby[pickup.id]}
                            downloadingReceipt={downloadingReceipt}
                            downloadingTicket={downloadingTicket}
                            onCompletePickup={onCompletePickup}
                            onFailPickup={onFailPickup}
                            onSignPickup={onSignPickup}
                            onToggleNearby={onToggleNearby}
                            onGenerateReceipt={onGenerateReceipt}
                            onGenerateTicket={onGenerateTicket}
                            onPrintTicket={onPrintTicket}
                            routeId={route.routeId}
                            onRefresh={onRefresh}
                            onAssignPickup={onAssignPickup}
                        />
                        {/* Absolute texto to indicate not initialized pickup */}
                        {
                            'planned' === pickupStatuses.find(status => status.value === pickup?.pickupStatus)?.value && (
                                <div className="absolute margin-auto top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-gray-500 font-bold text-xl">
                                    Recogida no inicializada
                                </div>
                            )
                        }   
                    </div>
                ))}
            </div>
        </div>
    );
};

export default RouteGroup;